/**
 * LiquidGlass Component - Mobile Optimized
 * 
 * A realistic liquid glass effect component that can be applied to various UI elements.
 * Uses advanced SVG filters for distortion effects and CSS for glass morphism styling.
 * 
 * Features:
 * - Realistic liquid distortion using fractal noise
 * - Advanced texture-based displacement for buttons
 * - Works with menus, popups, cards, and other elements
 * - Smooth glass morphism effects
 * - Customizable via className prop
 * 
 * Mobile Optimizations:
 * - Reduced backdrop blur on mobile for better performance
 * - Respects prefers-reduced-motion
 * - Touch-optimized interactions
 * - Responsive border radius
 * 
 * Based on modern macOS liquid glass effect implementation
 */

'use client';

import { cn } from '@/lib/utils';
import React from 'react';

export interface LiquidGlassProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'container' | 'element';
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'none';
}

// Mobile-first responsive border radius
const radiusClasses = {
  sm: 'rounded-md md:rounded-lg',
  md: 'rounded-lg md:rounded-xl',
  lg: 'rounded-xl md:rounded-2xl',
  xl: 'rounded-2xl md:rounded-3xl',
  full: 'rounded-full',
  none: 'rounded-none',
};

export function LiquidGlass({ 
  children, 
  className,
  variant = 'container',
  radius = 'xl'
}: LiquidGlassProps) {
  const variantClass = variant === 'container' ? 'liquid-glass-container' : 'liquid-glass-element';
  const radiusClass = radiusClasses[radius];

  return (
    <div className={cn(
      variantClass,
      radiusClass,
      'relative',
      'overflow-hidden',
      className
    )}>
      {children}
    </div>
  );
}

/**
 * LiquidGlassButton - A button with liquid glass effect
 */
export interface LiquidGlassButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'container' | 'element';
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'none';
}

export function LiquidGlassButton({ 
  children, 
  className,
  variant = 'element',
  radius = 'full',
  ...props 
}: LiquidGlassButtonProps) {
  const variantClass = variant === 'container' ? 'liquid-glass-container' : 'liquid-glass-element';
  const radiusClass = radiusClasses[radius];

  return (
    <button
      className={cn(
        variantClass,
        radiusClass,
        'relative z-10 overflow-hidden',
        'transition-all duration-200',
        'motion-safe:hover:scale-[1.03] motion-safe:active:scale-95',
        'touch-manipulation min-h-[44px]',
        'active:opacity-90',
        className
      )}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * LiquidGlassCard - A card with liquid glass effect
 */
export interface LiquidGlassCardProps {
  children: React.ReactNode;
  className?: string;
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'none';
}

export function LiquidGlassCard({ 
  children, 
  className,
  radius = 'xl'
}: LiquidGlassCardProps) {
  const radiusClass = radiusClasses[radius];

  return (
    <div className={cn(
      'liquid-glass-container',
      radiusClass,
      'overflow-hidden',
      'p-4 md:p-5 lg:p-6',
      className
    )}>
      {children}
    </div>
  );
}

/**
 * LiquidGlassMenu - A menu with liquid glass effect
 */
export interface LiquidGlassMenuProps {
  children: React.ReactNode;
  className?: string;
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'none';
}

export function LiquidGlassMenu({ 
  children, 
  className,
  radius = 'xl'
}: LiquidGlassMenuProps) {
  const radiusClass = radiusClasses[radius];

  return (
    <div className={cn(
      'liquid-glass-container',
      radiusClass,
      'overflow-hidden',
      'shadow-2xl',
      className
    )}>
      {children}
    </div>
  );
}

/**
 * LiquidGlassModal - A modal with liquid glass effect
 */
export interface LiquidGlassModalProps {
  children: React.ReactNode;
  className?: string;
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'none';
}

export function LiquidGlassModal({ 
  children, 
  className,
  radius = 'xl'
}: LiquidGlassModalProps) {
  const radiusClass = radiusClasses[radius];

  return (
    <div className={cn(
      'liquid-glass-container',
      radiusClass,
      'overflow-hidden',
      'shadow-2xl',
      'border border-white/20',
      className
    )}>
      {children}
    </div>
  );
}

